import { AdminBundle, AdminBundleGroup, AdminCategory, AdminItem, BundleGroupInput, BundleSource } from '../../../api/client';
import { dollarsInput, formatMoney } from '../../../shared/functions/formatter';

/** One "choose N from ..." requirement group, while it is being edited. */
export type GroupDraft = {
  /** Stable React key; also used to find the group a modal belongs to. */
  key: string;
  source: BundleSource;
  quantity: number;
  /** 0 means "not chosen yet". */
  categoryId: number;
  tagId: number;
  /** For a SELECTED_ITEMS group: the chosen member ids. */
  itemIds: number[];
};

/** The whole editable bundle as one object, so a field cannot drift alone. */
export type BundleFormState = {
  name: string;
  /** Dollars as typed by the operator; converted to cents on submit. */
  price: string;
  active: boolean;
  groups: GroupDraft[];
};

/** One group plus the pricing figures the current catalogue gives it. */
export type GroupStats = {
  group: GroupDraft;
  eligible: AdminItem[];
  min: number;
  max: number;
  count: number;
};

/** The setter every form field shares. */
export type SetBundleField = <K extends keyof BundleFormState>(key: K, value: BundleFormState[K]) => void;

let groupSequence = 0;

/** A fresh category group; the first category is a sensible starting point. */
export function emptyGroup(categories: AdminCategory[]): GroupDraft {
  groupSequence += 1;
  return {
    key: 'group-' + groupSequence,
    source: 'category',
    quantity: 1,
    categoryId: categories[0]?.id ?? 0,
    tagId: 0,
    itemIds: [],
  };
}

/** The form's shape of a saved bundle, for editing. */
export function draftFromBundle(bundle: AdminBundle): BundleFormState {
  return {
    name: bundle.name,
    price: dollarsInput(bundle.price_cents),
    active: bundle.active,
    groups: bundle.groups.map((group, index) => ({
      key: 'group-' + (group.id ?? index),
      source: group.source,
      quantity: group.quantity,
      categoryId: group.category_id ?? 0,
      tagId: group.tag_id ?? 0,
      itemIds: [...group.item_ids],
    })),
  };
}

/** A group draft on the wire: exactly one target, matching its source. */
export function toInput(groups: GroupDraft[]): BundleGroupInput[] {
  return groups.map((group) => {
    if (group.source === 'category') {
      return { quantity: group.quantity, source: 'category' as const, category_id: group.categoryId };
    }
    if (group.source === 'tag') {
      return { quantity: group.quantity, source: 'tag' as const, tag_id: group.tagId };
    }
    return { quantity: group.quantity, source: 'items' as const, item_ids: group.itemIds };
  });
}

/** The items a group can currently draw from (hidden items included). */
export function eligibleFor(group: GroupDraft, items: AdminItem[], itemById: Map<number, AdminItem>): AdminItem[] {
  if (group.source === 'category') {
    return group.categoryId === 0 ? [] : items.filter((item) => item.category_id === group.categoryId);
  }
  if (group.source === 'tag') {
    return group.tagId === 0 ? [] : items.filter((item) => item.tag_ids.includes(group.tagId));
  }
  return group.itemIds.map((id) => itemById.get(id)).filter((item): item is AdminItem => item !== undefined);
}

/**
 * The cheapest and dearest `quantity` units a group can draw. Repeats are
 * always allowed, so the bounds are simply quantity times the cheapest or
 * dearest eligible price.
 */
export function rangeFor(group: GroupDraft, items: AdminItem[], itemById: Map<number, AdminItem>): GroupStats {
  const eligible = eligibleFor(group, items, itemById);
  if (eligible.length === 0) return { group, eligible, min: 0, max: 0, count: 0 };
  let low = Infinity;
  let high = 0;
  for (const item of eligible) {
    low = Math.min(low, item.price_cents);
    high = Math.max(high, item.price_cents);
  }
  return { group, eligible, min: low * group.quantity, max: high * group.quantity, count: eligible.length };
}

/** Normalise a possibly absent id to the 0 the drafts use for "not chosen". */
function sameId(a: number | null | undefined, b: number | null | undefined): boolean {
  return (a ?? 0) === (b ?? 0);
}

/** Compare two id lists as sets, so reordering is not a change. */
function sameIdSet(a: number[] | null | undefined, b: number[] | null | undefined): boolean {
  const left = new Set(a ?? []);
  const right = new Set(b ?? []);
  if (left.size !== right.size) return false;
  for (const id of left) {
    if (!right.has(id)) return false;
  }
  return true;
}

/**
 * Compare two editable bundles field by field.
 *
 * Replaces the old serialised-key comparison: it names what changed, ignores
 * the render-only group key, and compares item ids as a set so reordering is
 * not dirty.
 */
export function isSameBundleForm(a: BundleFormState, b: BundleFormState): boolean {
  if (a.name !== b.name || a.price !== b.price || a.active !== b.active) return false;
  if (a.groups.length !== b.groups.length) return false;
  return a.groups.every((group, index) => {
    const other = b.groups[index];
    return (
      group.source === other.source &&
      group.quantity === other.quantity &&
      sameId(group.categoryId, other.categoryId) &&
      sameId(group.tagId, other.tagId) &&
      sameIdSet(group.itemIds, other.itemIds)
    );
  });
}

export function plural(count: number, word: string): string {
  return count + ' ' + word + (count === 1 ? '' : 's');
}

/** A readable name for one saved group, used by the summary and the preview. */
export function adminGroupLabel(group: AdminBundleGroup): string {
  if (group.source === 'category') return group.category_name ?? 'a category';
  if (group.source === 'tag') return group.tag_name ?? 'a tagged item';
  if (group.item_ids.length === 1) return group.items[0]?.title ?? 'selected items';
  return plural(group.item_ids.length, 'selected item');
}

export function rangeText(low: number, high: number): string {
  return low === high ? formatMoney(low) : formatMoney(low) + ' – ' + formatMoney(high);
}
